/**
 * @author       denys.yefremov@startupsoft.co (Denys Yefremov)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { is404Error } from '@zcode/error-filters'
import { definitelyError, isFailedToFetchError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import type { SchoolDistrictLevels, SchoolLevelsWidget, State } from '@zcode/state-type'
import type { AnyAction } from 'redux'
import type { ThunkAction } from 'redux-thunk'
import { logger } from '../../logger'
import {
  doneFetchingSchoolWidgetSchools,
  errorFetchingSchoolWidgetSchools,
  setSchoolWidgetSchoolsData,
  startFetchingSchoolWidgetSchools,
} from '../actions/schoolWidget'

interface loadSchoolWidgetSchoolsArgs {
  city?: string
  districtId: string
  neighborhood?: string
  schoolsLimit?: number
  page?: number
  schoolLevel: SchoolLevelsWidget
}

const loadSchoolWidgetDistrictSchools =
  ({
    city,
    schoolLevel,
    districtId,
    neighborhood,
    schoolsLimit = 5,
    page = 1,
  }: loadSchoolWidgetSchoolsArgs): ThunkAction<void, State, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(startFetchingSchoolWidgetSchools())

    try {
      const offset = (page - 1) * schoolsLimit
      const response = await Api.widgetDistrictSchools.get({
        city,
        districtId,
        neighborhood,
        schoolLevel,
        offset,
        limit: schoolsLimit,
      })

      // fix TypeScript in ZUM-53899
      dispatch(
        setSchoolWidgetSchoolsData({
          [schoolLevel]: response.schools,
        } as unknown as SchoolDistrictLevels)
      )
    } catch (err) {
      if (!is404Error(err) && !isFailedToFetchError(err)) {
        // @ts-expect-error
        dispatch(errorFetchingSchoolWidgetSchools(err.message ?? err))
        const msg = 'Failed to get schools data loadlSchoolWidgetSchools'
        logger.error(msg, city, districtId, definitelyError(msg, err))
      }
    } finally {
      dispatch(doneFetchingSchoolWidgetSchools())
    }
  }

// eslint-disable-next-line import/no-default-export
export default loadSchoolWidgetDistrictSchools
